import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  filtrosParaSalvar,
  useAlterarVisao,
  useApagarVisao,
  useSalvarVisao,
  useVisoes,
  type ModoDaFila,
  type VisaoDaFila,
} from "../../nucleo/tickets-operacao";
import type { EstadoDoTicket, UrgenciaDoTicket } from "../../nucleo/tickets";
import { Aviso, Botao, Campo } from "../ui";

export interface FiltrosDaFila {
  texto: string;
  estado: EstadoDoTicket | "";
  urgencia: UrgenciaDoTicket | "";
  equipe_id: string;
  responsavel_id: string;
  tag: string;
}

interface Props {
  filtros: FiltrosDaFila;
  modo: ModoDaFila;
  aoAplicar: (visao: VisaoDaFila) => void;
}

/** Guarda o conjunto de filtros da fila com nome — pessoal ou compartilhado com a equipe. */
export function VisoesSalvas({ filtros, modo, aoAplicar }: Props) {
  const visoes = useVisoes();
  const salvar = useSalvarVisao();
  const alterar = useAlterarVisao();
  const apagar = useApagarVisao();
  const [escolhidaId, setEscolhidaId] = useState("");
  const [nome, setNome] = useState("");
  const [compartilhar, setCompartilhar] = useState(false);
  const [renomeando, setRenomeando] = useState(false);
  const [novoNome, setNovoNome] = useState("");

  const escolhida = visoes.data?.find((item) => item.id === escolhidaId);
  const atuais = filtrosParaSalvar(filtros);

  function escolher(id: string) {
    setEscolhidaId(id);
    setRenomeando(false);
    const visao = visoes.data?.find((item) => item.id === id);
    if (visao) aoAplicar(visao);
  }

  function guardar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate(
      { nome: nome.trim(), modo, filtros: atuais, compartilhada: compartilhar },
      {
        onSuccess: (criada) => {
          setNome("");
          setCompartilhar(false);
          setEscolhidaId(criada.id);
        },
      },
    );
  }

  function renomear(evento: FormEvent) {
    evento.preventDefault();
    if (!escolhida) return;
    alterar.mutate(
      { id: escolhida.id, campos: { nome: novoNome.trim() } },
      { onSuccess: () => setRenomeando(false) },
    );
  }

  const erro = visoes.error ?? salvar.error ?? alterar.error ?? apagar.error;

  return (
    <div className="mb-4 border-b border-borda pb-4" data-testid="visoes-salvas">
      <div className="grid gap-3 md:grid-cols-3">
        <label className="campo" htmlFor="visao-salva-da-fila">
          <span className="campo__rotulo">Visão salva</span>
          <select
            id="visao-salva-da-fila"
            className="campo__controle"
            value={escolhidaId}
            onChange={(evento) => escolher(evento.target.value)}
          >
            <option value="">Nenhuma escolhida</option>
            {visoes.data?.map((visao) => (
              <option value={visao.id} key={visao.id}>
                {visao.nome}
                {visao.compartilhada ? " · da equipe" : ""}
                {!visao.minha && visao.dono_nome ? ` (${visao.dono_nome})` : ""}
              </option>
            ))}
          </select>
        </label>
        <form className="flex flex-wrap items-end gap-2 md:col-span-2" onSubmit={guardar}>
          <Campo
            rotulo="Nome para guardar estes filtros"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            maxLength={80}
            classeDaMoldura="flex-1"
          />
          <label className="flex items-center gap-2 pb-3">
            <input type="checkbox" checked={compartilhar} onChange={(evento) => setCompartilhar(evento.target.checked)} />
            Compartilhar com a equipe
          </label>
          <Botao type="submit" disabled={nome.trim().length < 2 || salvar.isPending}>
            Guardar visão
          </Botao>
        </form>
      </div>

      {escolhida?.minha ? (
        <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="gestao-da-visao">
          {renomeando ? (
            <form className="flex flex-wrap items-end gap-2" onSubmit={renomear}>
              <Campo
                rotulo="Novo nome da visão"
                value={novoNome}
                onChange={(evento) => setNovoNome(evento.target.value)}
                maxLength={80}
              />
              <Botao type="submit" disabled={novoNome.trim().length < 2 || alterar.isPending}>Salvar nome</Botao>
              <Botao variante="fantasma" onClick={() => setRenomeando(false)}>Cancelar</Botao>
            </form>
          ) : (
            <>
              <Botao
                variante="fantasma"
                onClick={() => {
                  setNovoNome(escolhida.nome);
                  setRenomeando(true);
                }}
              >
                Renomear visão
              </Botao>
              <Botao
                variante="fantasma"
                onClick={() => alterar.mutate({ id: escolhida.id, campos: { filtros: atuais, modo } })}
                disabled={alterar.isPending}
              >
                Atualizar com os filtros atuais
              </Botao>
              <Botao
                variante="fantasma"
                onClick={() =>
                  alterar.mutate({ id: escolhida.id, campos: { compartilhada: !escolhida.compartilhada } })
                }
                disabled={alterar.isPending}
              >
                {escolhida.compartilhada ? "Deixar só para mim" : "Compartilhar com a equipe"}
              </Botao>
              <Botao
                variante="fantasma"
                onClick={() => apagar.mutate(escolhida.id, { onSuccess: () => setEscolhidaId("") })}
                disabled={apagar.isPending}
              >
                Apagar visão
              </Botao>
            </>
          )}
        </div>
      ) : escolhida ? (
        <p className="nota-rodape mt-3">Visão da equipe criada por {escolhida.dono_nome ?? "outra pessoa"}: só quem a criou altera.</p>
      ) : null}
      {erro ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(erro)}</Aviso> : null}
    </div>
  );
}
